import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  MenuItem,
  Select,
  FormControl,
  Grid2,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ConfigurationDetailsModal from "./ConfigurationDetailsModal"; // Optional: if you use it for naming configs
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { saveFieldMapping } from "../../services/webServices";

const theme = createTheme({
  typography: {
    fontFamily: "Manrope, Arial, sans-serif",
  },
});

const NewFieldMapper = ({
  issueType,
  taskType,
  setALMModalOpen,
  availableFields,
  setAvailableFields,
  toolName,
  projectName,
  setOpenNewFieldMappingModal,
  setSelectedMapping,
  qmentisFields,
}) => {
  const appFields = {
    "Test Cases": [
      { key: "Test Case Title", label: "Test Case Title*" },
      { key: "Priority", label: "Priority*" },
      { key: "Category", label: "Category*" },
      { key: "Test Objective", label: "Test Objective" },
      { key: "Pre-conditions", label: "Pre-conditions" },
      { key: "Test Steps", label: "Test Steps" },
      { key: "Expected Result", label: "Expected Result" },
      { key: "Post-conditions", label: "Post-conditions" },
    ],
    "User Stories": [
      { key: "Title", label: "Title*" },
      { key: "Description", label: "Description" },
      { key: "Acceptance criteria", label: "Acceptance Criteria" },
      { key: "Additional Information", label: "Additional Information" },
    ],
    "Bugs": [
      { key: "Title", label: "Title*" },
      { key: "Description", label: "Description" },
      { key: "Steps_To_Reproduce", label: "Steps to Reproduce" },
      {
        key: "Expected_Vs_Actual_Behavior",
        label: "Expected Vs Actual Behavior",
      },
      { key: "Evidences_TBD", label: "Evidence" },
      { key: "Business_Impact", label: "Business Impact" },
      { key: "Technical_Impact", label: "Technical Impact" },
      { key: "Workaround", label: "Workaround" },
      { key: "Severity_Level", label: "Severity Level" },
      {
        key: "Possible_Causes_For_Defect_Leakage",
        label: "Possible Causes for Defect Leakage",
      },
      { key: "Ideal_Test_Case", label: "Ideal Test Case" },
      {
        key: "Possible_Causes_For_Dev_Team",
        label: "Possible Causes for Dev Team",
      },
      {
        key: "Recommended_Fixes_For_Dev_Team",
        label: "Recommended Fixes for Dev Team",
      },
      { key: "Similar_Bugs", label: "Similar Bugs" },
      { key: "Additional_Information", label: "Additional Information" },
      {
        key: "Validation_And_Verification_Retesting",
        label: "Validation And Verification Retesting",
      },
    ],
  };

  let mappingSourceFields;
  if (toolName === "qTest") {
    mappingSourceFields = qmentisFields || [];
  } else {
    // Use your static config for other tools
    mappingSourceFields = appFields[issueType] || [];
  }

  const initialMapping = mappingSourceFields.reduce((acc, field) => {
    acc[field.key] = null;
    return acc;
  }, {});
  const [mapping, setMapping] = useState(initialMapping);
  const [mappingName, setMappingName] = useState("");
  const [configModalOpen, setConfigModalOpen] = useState(false);

  const [errorMessage, setErrorMessage] = useState(null);
  const [finalSaveLoading, setFinalSaveLoading] = useState(false);

  const isSaveEnabled = () => {
    return Object.values(mapping).some((eachMap) => eachMap !== "");
  };

  const handleCloseModal = () => {
    setALMModalOpen(false);
  };

  const handleChange = (key) => (event) => {
    if (taskType === "Export") {
      setMapping((prevMapping) => ({
        ...prevMapping,
        [key]: event.target.value,
      }));
    } else {
      const newValue = event.target.value;
      const oldValue = mapping[key];

      // Update mapping
      setMapping((prevMapping) => ({
        ...prevMapping,
        [key]: newValue,
      }));

      // Update availableFields
      setAvailableFields((prevFields) => {
        let updatedFields = [...prevFields];

        // Add old value back if it exists and is not already in the list
        if (oldValue && !updatedFields.includes(oldValue)) {
          updatedFields.push(oldValue);
          updatedFields.sort((firstField, nextField) =>
            firstField.name
              .toLowerCase()
              .localeCompare(nextField.name.toLowerCase())
          );
        }

        // Remove the new selected value
        updatedFields = updatedFields.filter((field) => field !== newValue);

        return updatedFields;
      });
    }
  };

  const handleCancel = () => {
    setOpenNewFieldMappingModal(false);
  };

  const handleSaveClick = () => {
    setErrorMessage(null);

    if (toolName === "Test Rail") {
      // For TestRail -> Title OR Test Case Title + Priority + Category
      if (
        (!!mapping["Title"] || !!mapping["Test Case Title"]) &&
        !!mapping["Priority"] &&
        !!mapping["Category"]
      ) {
        setConfigModalOpen(true);
      } else {
        setErrorMessage("Please map mandatory fields for TestRail");
      }
    } else {
      // For other tools -> Only Title OR Test Case Title
      if (!!mapping["Title"] || !!mapping["Test Case Title"]) {
        setConfigModalOpen(true);
      } else {
        setErrorMessage("Please map mandatory fields");
      }
    }
  };

  const handleFinalSave = async () => {
    setFinalSaveLoading(true);
    try {
      let apiIssueType = issueType;
      if (toolName === "qTest") {
        const mappingForQTest = {
          "Test Cases": "test-cases",
          "Bugs": "defects",
        };
        apiIssueType = mappingForQTest[issueType] || issueType;
      }
      const payload = {
        toolName,
        projectName,
        issueType: apiIssueType,
        mappingName,
        mapping,
        taskType,
      };
      await saveFieldMapping(payload);
      setSelectedMapping({ mapping_name: mappingName, mapping });
      setOpenNewFieldMappingModal(false);
    } catch (error) {
      setErrorMessage(error.response.data.message);
      setConfigModalOpen(false);
    } finally {
      setFinalSaveLoading(false);
    }
  };

  return (
    <>
      <ThemeProvider theme={theme}>
        <Modal open={true} onClose={handleCloseModal}>
          <Box
            sx={{
              width: 600,
              bgcolor: "background.paper",
              p: 4,
              mx: "auto",
              borderRadius: 2,
              boxShadow: 24,
              maxHeight: "80%",
              overflow: "auto",
              alignSelf: "center",
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Manrope",
                  fontWeight: 500,
                  fontStyle: "medium",
                  fontSize: "16px",
                  lineHeight: "24px",
                  letterSpacing: "0.15px",
                  textAlign: "center",
                  verticalAlign: "middle",
                }}
              >
                Field Mapping
              </Typography>

              <IconButton onClick={handleCloseModal} sx={{ padding: "4px" }}>
                <CloseIcon
                  color="#171C1E"
                  fontSize="14px"
                  sx={{ color: "#171C1E", fontWeight: "500" }}
                />
              </IconButton>
            </Box>

            <Grid2 container spacing={2} mt={2}>
              {mappingSourceFields.map((field) => (
                <Grid2
                  container
                  direction={"row"}
                  spacing={2}
                  key={field.key}
                  sx={{ width: "100%" }}
                  justifyContent="space-between"
                >
                  <Grid2
                    xs={4}
                    display="flex"
                    alignItems="center"
                    maxWidth={"35%"}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Manrope",
                        fontWeight: 400,
                        fontStyle: "normal", // changed from "regular"
                        fontSize: "14px",
                        lineHeight: "24px",
                        letterSpacing: "0.15px",
                        verticalAlign: "middle",
                        whiteSpace: "nowrap",
                        overflow: "hidden", // required for ellipsis
                        textOverflow: "ellipsis",
                        display: "inline-block", // ensures ellipsis works
                        maxWidth: "100%", // example, needed to trigger ellipsis
                      }}
                    >
                      {field.label}
                    </Typography>
                  </Grid2>
                  <Grid2 width={"60%"}>
                    <FormControl fullWidth size="small">
                      <Select
                        value={mapping[field.key]}
                        onChange={handleChange(field.key)}
                        displayEmpty
                      >
                        <MenuItem value={null}>
                          <em>Select Field</em>
                        </MenuItem>
                        {(taskType === "Import"
                          ? [mapping[field.key], ...availableFields]
                          : [...availableFields]
                        )
                          .filter(Boolean)
                          .map((eachField) => (
                            <MenuItem key={eachField.name} value={eachField}>
                              {eachField.name}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid2>
                </Grid2>
              ))}
            </Grid2>

            {!!errorMessage && (
              <Typography
                mt={2}
                sx={{
                  fontFamily: "Manrope",
                  fontWeight: 300,
                  fontStyle: "medium",
                  fontSize: "14px",
                  lineHeight: "20px",
                  letterSpacing: "0.15px",
                  verticalAlign: "middle",
                  color: "red",
                  textAlign: "right",
                }}
              >
                {errorMessage}
              </Typography>
            )}

            <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
              <Button
                onClick={handleCancel}
                sx={{
                  color: "#49A3FF",
                  textTransform: "none",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#49A3FF",
                  color: "white",
                  textTransform: "none",
                }}
                onClick={() => handleSaveClick()} // optional
                disabled={!isSaveEnabled()} // 👈 disable if required fields are not filled
              >
                Save
              </Button>
            </Box>
          </Box>
        </Modal>
      </ThemeProvider>

      {/* Optional: ask for config name before saving */}
      <ConfigurationDetailsModal
        open={configModalOpen}
        onClose={() => setConfigModalOpen(false)}
        onSave={handleFinalSave}
        setMappingName={setMappingName}
        mappingName={mappingName}
        finalSaveLoading={finalSaveLoading}
      />
    </>
  );
};

export default NewFieldMapper;
